// src/pages/OldPhotosRestoration.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import GenerateOldPhotosRestorationModal from "../components/GenerateOldPhotosRestorationModal";
import placeholderImage from "../assets/placeholder.jpg";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import Loader from "../components/Loader";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

const FEATURE = "old-photos";
const PAGE_SIZE = 12;

/* ------------------------------ Mapping ------------------------------ */
/**
 * Server assets -> UI items
 * - keeps: pending/queued/processing/finished/failed
 * - hides: deleted (soft-deleted)
 */
function mapOldPhotosAssets(assets) {
  return (assets || [])
    .map((a) => {
      let md = a?.metadata;
      if (typeof md === "string") {
        try {
          md = JSON.parse(md);
        } catch {
          md = {};
        }
      }
      return { ...a, metadata: md || {} };
    })
    .filter((a) => String(a?.metadata?.feature || "") === FEATURE)
    // hide soft-deleted
    .filter((a) => {
      const st = String(a?.metadata?.status || "").toLowerCase();
      const del = a?.metadata?.deleted;
      return st !== "deleted" && del !== true && del !== "true";
    })
    .map((a) => {
      const statusRaw = String(a?.metadata?.status || "").toLowerCase();
      const urlStr = typeof a?.url === "string" ? a.url : "";
      const pending =
        statusRaw === "pending" ||
        statusRaw === "queued" ||
        statusRaw === "processing" ||
        urlStr.includes("#") ||
        !urlStr;
      const failed = statusRaw === "failed" || statusRaw === "error";
      return {
        id: a.id,
        url: urlStr || "",
        status: failed ? "failed" : pending ? "pending" : "done",
        createdAt: a.updated_at || a.created_at || a.createdAt || 0,
        name: a.name || "Old Photo",
        reason: a?.metadata?.reason || a?.metadata?.error || null,
      };
    })
    .sort((l, r) => new Date(r.createdAt) - new Date(l.createdAt));
}

export default function OldPhotosRestoration() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const projectId = selectedProduct?.id || paramProjectId;

  // items: [{id,url,status,reason}]
  const [items, setItems] = useState([]);
  const [productImages, setProductImages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);

  // quota
  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    blocked: false,
    block_reason: null,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
    period_start: null,
    period_end: null,
    interval: "month",
    shift_months: 0,
  });

  // infinite scroll state (✅ matched to ObjectRemover pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const previewBackdrop = useRef(null);
  const pollRef = useRef(null);

  /* ------------------------------ Helpers ------------------------------ */
  const toArray = useCallback((val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      const t = val.trim();
      if (!t || t === "{}") return [];
      if (t.startsWith("{") && t.endsWith("}")) {
        return t
          .slice(1, -1)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      try {
        const parsed = JSON.parse(t);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  const formatIsoDate = useCallback((iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const toCloudinaryDownloadUrl = useCallback((url) => {
    if (!url) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes("cloudinary") && u.pathname.includes("/upload/")) {
        u.pathname = u.pathname.replace("/upload/", "/upload/fl_attachment/");
        return u.toString();
      }
    } catch {
      // ignore parse errors
    }
    return url;
  }, []);

  const visibleItems = useMemo(
    () => items.slice(0, page * PAGE_SIZE),
    [items, page]
  );

  // ✅ stable (DO NOT depend on `page`) — matches ObjectRemover
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  const anyPending = useMemo(
    () =>
      items.some(
        (x) =>
          x.status === "pending" ||
          !x.url ||
          x.url === "/spinner.gif" ||
          (typeof x.url === "string" && x.url.includes("#")) ||
          String(x.id).startsWith("tmp-")
      ),
    [items]
  );

  /* --------------------------- Quota fetcher --------------------------- */
  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_BASE}/api/old-photos/quota`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  /* --------------------------- Fetch project data --------------------------- */
  const fetchProject = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed product, don't error — keep CTA visible and stop loading.
      if (!projectId) {
        setProductImages([]);
        setItems((prev) => prev.filter((p) => String(p.id).startsWith("tmp-"))); // keep optimistic only
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) {
        setInitialLoading(true);
        setPage(1);
        pageRef.current = 1;
      }

      try {
        const token = await getToken();
        const res = await axios.get(`${API_BASE}/api/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // analyzed images for the modal
        const imgs = toArray(res.data.images);
        setProductImages(imgs);

        // generated assets
        const serverList = mapOldPhotosAssets(res.data.assets || []);

        setItems((prev) => {
          const temps = prev.filter((p) => String(p.id).startsWith("tmp-"));
          const serverById = new Map(serverList.map((a) => [String(a.id), a]));
          const keptTemps = temps.filter((t) => !serverById.has(String(t.id)));
          const merged = [...keptTemps, ...serverList];

          recalcHasMore(merged, silent ? pageRef.current : 1);
          return merged;
        });

        setError(null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Fetch project failed:", e?.response?.data || e?.message);
        if (!silent) {
          setError(
            e?.response?.status === 401
              ? "Session expired. Please sign in again."
              : "Failed to fetch project data"
          );
        }
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId, recalcHasMore, toArray]
  );

  // Keep pageRef in sync (so merges can compute hasMore without causing refetch loops)
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // Also keep hasMore in sync whenever page/items change
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < items.length);
  }, [page, items.length]);

  // Initial load
  useEffect(() => {
    void fetchProject(false);
    void fetchQuota();
  }, [fetchProject, fetchQuota]);

  // Keep route in sync with selected product
  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/old-photos-restoration/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  // Clear poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  /* ----------------------- Polling (auto-refresh) ----------------------- */
  const beginPolling = useCallback(() => {
    if (!projectId) return;

    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    let attempts = 0;

    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        await fetchProject(true);

        setItems((prev) => {
          const localPending = prev.some(
            (x) =>
              x.status === "pending" ||
              !x.url ||
              x.url === "/spinner.gif" ||
              (typeof x.url === "string" && x.url.includes("#")) ||
              String(x.id).startsWith("tmp-")
          );

          if (!localPending || attempts >= 40) {
            clearInterval(pollRef.current);
            pollRef.current = null;

            void fetchQuota();

            // cleanup stale temps if timed out
            if (attempts >= 40) {
              const cleaned = prev.filter((x) => !String(x.id).startsWith("tmp-"));
              if (cleaned.length !== prev.length) {
                setError("Generation timed out. Please try again.");
              }
              return cleaned;
            }
          }

          return prev;
        });
      } catch {
        if (attempts >= 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;

          setItems((prev) => {
            const cleaned = prev.filter((x) => !String(x.id).startsWith("tmp-"));
            if (cleaned.length !== prev.length) {
              setError("Generation timed out. Please try again.");
            }
            return cleaned;
          });

          void fetchQuota();
        }
      }
    }, 5000);
  }, [fetchProject, fetchQuota, projectId]);

  useEffect(() => {
    if (anyPending && projectId) beginPolling();
  }, [anyPending, beginPolling, projectId]);

  /* ------------------ Generate handler (enqueue job) ------------------ */
  const handleGenerate = useCallback(
    async (imageUrl, quality = "2k", colorFlag = true) => {
      setError(null);

      // ✅ No analyzed product → route to Analyze (CTA stays visible on the page)
      if (!projectId) {
        setError("Please analyze a product first so we can save your generated assets.");
        navigate("/analyze");
        return;
      }

      const canGenerateNow =
        !quota.blocked && (quota.unlimited || (quota.remaining || 0) > 0);

      if (!canGenerateNow) {
        navigate("/pricing");
        return;
      }

      const tempId = `tmp-${Date.now()}`;

      try {
        // optimistic spinner
        setItems((prev) => {
          const next = [
            { id: tempId, url: "/spinner.gif", status: "pending", reason: null },
            ...prev,
          ];
          setPage(1);
          pageRef.current = 1;
          recalcHasMore(next, 1);
          return next;
        });

        // optimistic quota decrement (pending counts toward usage)
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: Math.max(0, (q.remaining || 0) - 1),
            used: (q.used || 0) + 1,
          }));
        }

        beginPolling();

        const token = await getToken();
        const res = await axios.post(
          `${API_BASE}/api/old-photos/restore`,
          { imageUrl, projectId, quality, color_flag: !!colorFlag },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // replace temp card id with real asset id
        const placeholder = res?.data?.asset;
        if (placeholder?.id) {
          setItems((prev) =>
            prev.map((it) =>
              it.id === tempId
                ? {
                    ...it,
                    id: placeholder.id,
                    url: placeholder.url || it.url || "/spinner.gif",
                    status: "pending",
                    reason: null,
                  }
                : it
            )
          );
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Generate failed:", e?.response?.data || e?.message);

        // remove local temp spinner
        setItems((prev) => {
          const next = prev.filter((img) => img.id !== tempId);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // revert optimistic quota decrement if the queue failed
        if (!quota.unlimited) {
          setQuota((q) => ({
            ...q,
            remaining: (q.remaining || 0) + 1,
            used: Math.max(0, (q.used || 0) - 1),
          }));
        }

        const code = e?.response?.data?.code;
        if (
          code === "payment_required" ||
          code === "images_quota_exceeded" ||
          code === "blocked"
        ) {
          void fetchQuota();
          navigate("/pricing");
          return;
        }

        setError(e?.response?.data?.error || "Failed to restore old photo.");
        void fetchQuota();
      }
    },
    [
      getToken,
      projectId,
      beginPolling,
      recalcHasMore,
      quota,
      navigate,
      fetchQuota,
    ]
  );

  /* ------------------------------ Helpers ------------------------------ */
  const handleDownload = useCallback(
    (url) => {
      const dl = toCloudinaryDownloadUrl(url);
      const a = document.createElement("a");
      a.href = dl;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [toCloudinaryDownloadUrl]
  );

  const handleDelete = useCallback(
    async (assetId) => {
      if (String(assetId).startsWith("tmp-")) {
        setItems((prev) => {
          const next = prev.filter((i) => i.id !== assetId);
          recalcHasMore(next, pageRef.current);
          return next;
        });
        return;
      }

      if (!window.confirm("Delete this image?")) return;

      try {
        const token = await getToken();
        await axios.delete(`${API_BASE}/api/old-photos/delete`, {
          data: { assetId },
          headers: { Authorization: `Bearer ${token}` },
        });

        setItems((prev) => {
          const next = prev.filter((i) => i.id !== assetId);
          if (preview?.id === assetId) setPreview(null);
          recalcHasMore(next, pageRef.current);
          return next;
        });

        // quota should NOT decrease on delete; still refresh for consistency
        void fetchQuota();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Delete failed:", e?.response?.data || e?.message);
        // eslint-disable-next-line no-alert
        alert("Delete failed");
      }
    },
    [getToken, preview, recalcHasMore, fetchQuota]
  );

  /* --------------------------- Infinite Scroll --------------------------- */
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  // Preview: close on Esc / click-outside
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current)
        setPreview(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  // Derived display values for quota
  const canGenerate =
    !quota.blocked && (quota.unlimited || (quota.remaining || 0) > 0);

  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);

  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  const shiftMonths = Number(quota.shift_months || 0);

  const isInitialLoading = initialLoading && !error && items.length === 0;

  return (
    <div className="w-full px-4 sm:px-8 py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-8 py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h1 className="text-3xl font-bold text-white">Old Photos Restoration</h1>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : quota.blocked
                  ? "border-red-500/60 bg-red-500/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : quota.blocked
                  ? quota.block_reason === "payment_required"
                    ? "Payment required to continue"
                    : "Account blocked"
                  : `Cycle: ${formatIsoDate(quota.period_start)} → ${formatIsoDate(
                      quota.period_end
                    )}${shiftMonths ? ` (time shift: ${shiftMonths > 0 ? "+" : ""}${shiftMonths} months)` : ""}`
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : quota.blocked ? (
                <span className="text-red-300 font-semibold text-sm sm:text-base">
                  {quota.block_reason === "payment_required"
                    ? "Payment required"
                    : "Account blocked"}
                </span>
              ) : (
                <>
                  <span className="text-white/80 text-sm sm:text-base">
                    <span className="font-semibold text-white">{remaining}</span>{" "}
                    left of{" "}
                    <span className="font-semibold text-white">{totalImages}</span>
                  </span>

                  {!!shiftMonths && (
                    <span className="text-xs px-2 py-1 rounded-full border border-pink-500/50 bg-pink-500/10 text-pink-300">
                      Time shift {shiftMonths > 0 ? "+" : ""}
                      {shiftMonths}m
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-3 mt-2">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages}</span>
            </div>
          </div>
        )}

        <p className="text-white/70 mb-6">
          Fix scratches, fading, and damage on vintage photos using AI.
        </p>

        {error && (
          <div className="mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flex items-center justify-center py-10 sm:py-16">
            <Loader />
          </div>
        ) : initialLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl border border-[#2A2A40] bg-[#1C1C34] h-[300px] animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {/* ✅ Create Card (always shown; redirects to Analyze or Pricing when needed) */}
            <div
              onClick={() => {
                if (!projectId) {
                  navigate("/analyze");
                  return;
                }
                if (!canGenerate) {
                  navigate("/pricing");
                  return;
                }
                setModalOpen(true);
              }}
              className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg h-[300px] min-h-[160px] transition text-center p-6 ${
                !projectId || !canGenerate
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
              }`}
              title={
                !projectId
                  ? "Analyze a product first to start generating"
                  : !canGenerate
                  ? "Out of images or blocked. Click to manage plan."
                  : "Choose an image and quality (2K/4K)"
              }
            >
              <Plus className="w-8 h-8 mb-2" />
              <p className="font-semibold text-base">Restore Old Photo</p>
              <p className="text-sm text-gray-400 mt-1">
                {!projectId
                  ? "Analyze your first product to get started"
                  : quota.unlimited
                  ? "Unlimited images"
                  : quota.blocked
                  ? quota.block_reason === "payment_required"
                    ? "Payment required to continue"
                    : "Account blocked"
                  : `You have ${remaining} left of ${totalImages} this cycle`}
              </p>
              {!!projectId && !canGenerate && (
                <p className="text-xs text-red-300 mt-2">
                  {quota.block_reason === "payment_required"
                    ? "Update your subscription to continue."
                    : "You’ve reached your limit or your account is blocked."}
                </p>
              )}
            </div>

            {visibleItems.map(({ id, url, status, reason }) => {
              const isTmp = String(id).startsWith("tmp-");
              const isPending =
                status === "pending" ||
                !url ||
                url.includes("#") ||
                url === "/spinner.gif" ||
                isTmp;

              const isFailed = status === "failed";
              const displayUrl = isPending ? "/spinner.gif" : url;

              return (
                <div
                  key={id}
                  className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-3 flex items-center justify-center transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px]"
                  style={{ overflow: "hidden" }}
                >
                  {isFailed ? (
                    <div className="flex flex-col items-center justify-center w-full h-full text-center px-3">
                      <AlertTriangle size={28} className="text-pink-400 mb-2" />
                      <div className="font-semibold text-white">
                        Generation failed
                      </div>
                      <div className="text-xs text-white/60 mt-2">
                        {reason || "Could not restore this photo."}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(id)}
                        className="mt-4 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm shadow transition"
                      >
                        Delete
                      </button>
                    </div>
                  ) : isPending ? (
                    <>
                      <img
                        src="/spinner.gif"
                        alt="Generating..."
                        className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div className="absolute top-2 right-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(id);
                          }}
                          className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={displayUrl}
                        alt="Restored result"
                        className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                        onClick={() => setPreview({ url: displayUrl, id })}
                        onError={(e) => {
                          // eslint-disable-next-line no-param-reassign
                          e.currentTarget.src = placeholderImage;
                        }}
                        draggable={false}
                        style={{
                          background: "#111124",
                          height: "100%",
                          maxHeight: "calc(100% - 32px)",
                        }}
                      />
                      <div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                        style={{ background: "rgba(21, 21, 43, 0.15)" }}
                      >
                        <div className="flex gap-3 pointer-events-auto">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(displayUrl);
                            }}
                            className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Download"
                          >
                            <Download size={20} />
                          </button>
                          <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Open in new tab"
                          >
                            <ExternalLink size={20} />
                          </a>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !initialLoading && (
          <div
            ref={loaderRef}
            className="w-full flex justify-center py-8 pointer-events-none"
          >
            {loadingMore && (
              <img src="/spinner.gif" alt="Loading..." className="w-7 h-7 mx-auto" />
            )}
          </div>
        )}

        {modalOpen && (
          <GenerateOldPhotosRestorationModal
            productImages={productImages}
            onClose={() => setModalOpen(false)}
            onGenerate={handleGenerate}
          />
        )}

        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>
              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Restored result preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>
              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => {
                    handleDownload(preview.url);
                    setPreview(null);
                  }}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
